const kOberinAnchorYear = 2026;
const kOberinBaseYear = 463;
const kOberinSecondsPerHour = 600;
const kOberinSecondsPerDay = 14400;

type MoonCycle = {
    name: string;
    tag: string;
};

type MoonBoundary = {
    cycle: MoonCycle;
    month: number;
    day: number;
};

const moonCycles = {
    fighting: { name: "Fighting Moon", tag: "fighting" },
    sleeping: { name: "Sleeping Moon", tag: "sleeping" },
    waking: { name: "Waking Moon", tag: "waking" },
    growing: { name: "Growing Moon", tag: "growing" },
    bleeding: { name: "Bleeding Moon", tag: "bleeding" },
    red: { name: "Red Moon", tag: "red" },
    harvest: { name: "Harvest Moon", tag: "harvest" },
    dancing: { name: "Dancing Moon", tag: "dancing" },
    lucky: { name: "Lucky Moon", tag: "lucky" },
    blue: { name: "Blue Moon", tag: "blue" },
};

const moonBoundaries: MoonBoundary[] = [
    { cycle: moonCycles.fighting, month: 1, day: 1 },
    { cycle: moonCycles.sleeping, month: 2, day: 8 },
    { cycle: moonCycles.waking, month: 3, day: 10 },
    { cycle: moonCycles.growing, month: 4, day: 11 },
    { cycle: moonCycles.bleeding, month: 5, day: 19 },
    { cycle: moonCycles.red, month: 7, day: 1 },
    { cycle: moonCycles.harvest, month: 8, day: 1 },
    { cycle: moonCycles.dancing, month: 9, day: 10 },
    { cycle: moonCycles.lucky, month: 10, day: 20 },
    { cycle: moonCycles.blue, month: 12, day: 1 },
];

function buildUtcSeconds(year: number, month: number, day: number): number {
    return Date.UTC(year, month - 1, day, 0, 0, 0) / 1000;
}

function resolveMoonBoundaryIndex(month: number, day: number): number {
    let boundaryIndex = 0;

    for (let i = 0; i < moonBoundaries.length; i++) {
        if (
            month > moonBoundaries[i].month ||
            (month === moonBoundaries[i].month && day >= moonBoundaries[i].day)
        ) {
            boundaryIndex = i;
        }
    }

    return boundaryIndex;
}

function resolveMoonBoundaryIndexByTag(tag: string): number {
    for (let i = 0; i < moonBoundaries.length; i++) {
        if (moonBoundaries[i].cycle.tag === tag) {
            return i;
        }
    }

    return 0;
}

function resolveOberinYearFromBoundaryIndex(
    utcYear: number,
    boundaryIndex: number,
): number {
    let oberinYearValue = utcYear - kOberinAnchorYear + kOberinBaseYear;
    const wakingBoundaryIndex = resolveMoonBoundaryIndexByTag("waking");

    if (boundaryIndex >= wakingBoundaryIndex) {
        oberinYearValue++;
    }

    return Math.max(0, Math.min(65535, oberinYearValue));
}

function getOberinDate(date: Date) {
    const utcYear = date.getUTCFullYear();
    const utcMonth = date.getUTCMonth() + 1;
    const utcDay = date.getUTCDate();
    const utcSeconds = Math.floor(date.getTime() / 1000);

    const boundaryIndex = resolveMoonBoundaryIndex(utcMonth, utcDay);
    const currentMoon = moonBoundaries[boundaryIndex].cycle;
    const currentYear = resolveOberinYearFromBoundaryIndex(
        utcYear,
        boundaryIndex,
    );

    const moonStartUtc = buildUtcSeconds(
        utcYear,
        moonBoundaries[boundaryIndex].month,
        moonBoundaries[boundaryIndex].day,
    );

    let nextBoundaryYear = utcYear;
    let nextBoundaryIndex = boundaryIndex + 1;
    if (nextBoundaryIndex >= moonBoundaries.length) {
        nextBoundaryIndex = 0;
        nextBoundaryYear++;
    }

    const nextMoonStartUtc = buildUtcSeconds(
        nextBoundaryYear,
        moonBoundaries[nextBoundaryIndex].month,
        moonBoundaries[nextBoundaryIndex].day,
    );

    const elapsedSinceMoonStart =
        utcSeconds >= moonStartUtc ? utcSeconds - moonStartUtc : 0;

    const currentDay =
        Math.floor(elapsedSinceMoonStart / kOberinSecondsPerDay) + 1;

    const oberinSecondsInCurrentDay =
        elapsedSinceMoonStart % kOberinSecondsPerDay;
    const currentHours = Math.floor(
        oberinSecondsInCurrentDay / kOberinSecondsPerHour,
    );
    const currentMinutes = Math.floor(
        (oberinSecondsInCurrentDay % kOberinSecondsPerHour) / 10,
    );

    const totalOberinDaysInMoon = Math.max(
        1,
        Math.floor((nextMoonStartUtc - moonStartUtc) / kOberinSecondsPerDay),
    );
    const segmentSize = Math.max(1, Math.floor(totalOberinDaysInMoon / 8));

    let moonState = 0;
    let segmentThreshold = segmentSize;
    while (segmentThreshold < currentDay && moonState < 7) {
        moonState++;
        segmentThreshold += segmentSize;
    }

    return {
        currentDay,
        currentHours,
        currentMinutes,
        currentMoon,
        moonState,
        currentYear,
    };
}

export default getOberinDate;
